import { useState, useEffect } from 'react';
import { Image } from '@/components/common/image';

export interface PixelRatioImageProps {
  images: string[]; // 图像数组，按索引顺序对应 1x, 2x, 3x, ..., 8x
  className?: string;
  width?: number;
  height?: number;
  alt?: string;
  style?: React.CSSProperties;
}

export const PixelRatioImage = ({
  images,
  className,
  width,
  height,
  alt = "Pixel Ratio Image",
  style
}: PixelRatioImageProps) => {
  const [devicePixelRatio, setDevicePixelRatio] = useState(1);

  useEffect(() => {
    // 获取设备像素比
    setDevicePixelRatio(window.devicePixelRatio || 1);

    // 监听设备像素比变化（比如用户缩放页面）
    const handlePixelRatioChange = () => {
      setDevicePixelRatio(window.devicePixelRatio || 1);
    };

    // 使用 matchMedia 监听像素比变化
    const mediaQuery = window.matchMedia(`(resolution: ${window.devicePixelRatio}dppx)`);
    mediaQuery.addEventListener('change', handlePixelRatioChange);

    return () => {
      mediaQuery.removeEventListener('change', handlePixelRatioChange);
    };
  }, []);

  // 根据设备像素比选择合适的图像
  const getImageSrc = () => {
    if (!images || images.length === 0) {
      return '';
    }

    // 根据设备像素比选择对应的图像索引
    const targetRatio = Math.ceil(devicePixelRatio);
    const imageIndex = Math.min(targetRatio - 1, images.length - 1);
    
    return images[imageIndex] || images[0];
  };

  // 生成 srcSet 用于响应式图像
  const generateSrcSet = () => {
    if (!images || images.length === 0) {
      return '';
    }

    return images
      .map((image, index) => `${image} ${index + 1}x`)
      .join(', ');
  };

  if (!images || images.length === 0) {
    return null;
  }

  return (
    <Image
      src={getImageSrc()}
      srcSet={generateSrcSet()}
      alt={alt}
      className={className}
      width={width}
      height={height}
      style={style}
    />
  );
}

export const displayName = "PixelRatioImage";